import { Box, type SxProps, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Typography } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import type { Property } from "csstype";
import { DateTime } from "luxon";
import type React from "react";
import { type CSSProperties, type FC, useMemo, useState } from "react";
import { INK, NEUTRAL } from "../../theme/tokens";
import type { Patient, PatientCoverage } from "./types";

// --- Sorting Types and Utility Functions ---

type Order = "asc" | "desc";
type Key = keyof Patient | "medicalConditionsString";

interface HeadCell {
	id: Key;
	label: string;
	numeric: boolean;
}

function descendingComparator<T>(a: T, b: T, orderBy: Key) {
	let aValue;
	let bValue;

	// Special handling for medicalConditions (joined as a string)
	if (orderBy === "medicalConditionsString") {
		aValue = (a as Patient).medicalConditions.join(", ");
		bValue = (b as Patient).medicalConditions.join(", ");
	} else {
		aValue = (a as any)[orderBy];
		bValue = (b as any)[orderBy];
	}

	// DateTime handling: compare by timestamp
	if (aValue && aValue.isDateTime) {
		aValue = aValue.valueOf();
		bValue = bValue.valueOf();
	}

	if (bValue < aValue) {
		return -1;
	}
	if (bValue > aValue) {
		return 1;
	}
	return 0;
}

function getComparator(order: Order, orderBy: Key): (a: Patient, b: Patient) => number {
	return order === "desc" ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
}

// Ensure stable sorting
function stableSort(array: Patient[], comparator: (a: Patient, b: Patient) => number) {
	const stabilizedThis = array.map((el, index) => [el, index] as [Patient, number]);
	stabilizedThis.sort((a, b) => {
		const order = comparator(a[0], b[0]);
		if (order !== 0) {
			return order;
		}
		return a[1] - b[1];
	});
	return stabilizedThis.map((el) => el[0]);
}

// --- Table Head Definition ---

const getHeadCells = (isHospitalDemo: boolean): HeadCell[] => {
	if (isHospitalDemo) {
		return [
			{ id: "patientName", numeric: false, label: "Patient Name" },
			{ id: "room", numeric: false, label: "Room" },
			{
				id: "medicalConditionsString",
				numeric: false,
				label: "Medical Condition",
			},
			{ id: "admissionDate", numeric: false, label: "Admission Date" },
			{ id: "nurseYesterday", numeric: false, label: "Last Shift Nurse" },
			{ id: "nurseToday", numeric: false, label: "Current Shift Nurse" },
			{ id: "patientScore", numeric: true, label: "Patient Score" },
		];
	}

	return [
		{ id: "patientName", numeric: false, label: "Patient Name" },
		{ id: "coverageType", numeric: false, label: "Coverage" },
		{
			id: "medicalConditionsString",
			numeric: false,
			label: "Medical Condition(s)",
		},
		{ id: "admissionDate", numeric: false, label: "Admission Date" },
		{ id: "chanceInTheFacility", numeric: true, label: "Chance In Facility (%)" },
	];
};
const cellsSx = {
	px: 1,
	py: 1,
	color: "#4D5579D9",
	fontSize: "14px",
	fontWeight: 500,
	lineHeight: "100%",
} as const satisfies SxProps;
const coverageToColor: Record<PatientCoverage, string> = {
	Rehabilitation: "#00A9ED80",
	"Long Term": "#4CA33180",
};

interface EnhancedTableHeadProps {
	onRequestSort: (event: React.MouseEvent<unknown>, property: Key) => void;
	order: Order;
	orderBy: Key;
	isHospitalDemo: boolean;
	// Strict inhouse2+hospital flag; drives the light-gray header (Team-page style).
	isRealHospitalDemo: boolean;
}

function EnhancedTableHead(props: EnhancedTableHeadProps) {
	const { order, orderBy, onRequestSort, isHospitalDemo, isRealHospitalDemo } = props;
	const createSortHandler = (property: Key) => (event: React.MouseEvent<unknown>) => {
		onRequestSort(event, property);
	};

	const headCells = getHeadCells(isHospitalDemo);

	return (
		<TableHead>
			<TableRow>
				{headCells.map((headCell) => (
					<TableCell
						sx={{
							// Demo: light-gray header + dark text (matches the Team page); others keep the green.
							backgroundColor: isRealHospitalDemo ? NEUTRAL[100] : "primary.main",
							color: isRealHospitalDemo ? INK.PRIMARY : "white",
							px: 1,
							fontWeight: 600,
						}}
						key={headCell.id}
						align={"left"}
						padding="normal"
						sortDirection={orderBy === headCell.id ? order : false}
					>
						<TableSortLabel
							active={orderBy === headCell.id}
							direction={orderBy === headCell.id ? order : "asc"}
							onClick={createSortHandler(headCell.id)}
							sx={{
								color: `${isRealHospitalDemo ? INK.PRIMARY : "white"} !important`,
								"& .MuiTableSortLabel-icon": {
									color: `${isRealHospitalDemo ? INK.PRIMARY : "white"} !important`,
								},
							}}
						>
							{headCell.label}
							{orderBy === headCell.id ? (
								<Box component="span" sx={visuallyHidden}>
									{order === "desc" ? "sorted descending" : "sorted ascending"}
								</Box>
							) : null}
						</TableSortLabel>
					</TableCell>
				))}
			</TableRow>
		</TableHead>
	);
}

// --- Name Masking Function ---
const maskPatientName = (name: string): string => {
	return name
		.split(" ")
		.map((word) => {
			if (word.length === 0) return word;
			const firstLetter = word[0];
			const dashes = "-".repeat(Math.max(1, word.length - 1));
			return `${firstLetter}${dashes}`;
		})
		.join(" ");
};

// --- Main Component ---
type PatientTableProps = {
	width?:
		| Property.Width
		| {
				xs?: CSSProperties["width"];
				mediumPhone?: CSSProperties["width"];
				sm?: CSSProperties["width"];
				md?: CSSProperties["width"];
				lg?: CSSProperties["width"];
				xl?: CSSProperties["width"];
				xxl?: CSSProperties["width"];
		  };
	height?: Property.Height;
	patientsData: Patient[];
	hidePatientNames?: boolean;
	isHospitalDemo?: boolean;
	isRealHospitalDemo?: boolean;
};
export const PatientTable: FC<PatientTableProps> = ({
	width = "100%",
	height = "100%",
	patientsData,
	hidePatientNames = false,
	isHospitalDemo = false,
	isRealHospitalDemo = false,
}) => {
	const [order, setOrder] = useState<Order>("asc");
	const [orderBy, setOrderBy] = useState<Key>("patientName");

	const handleRequestSort = (event: React.MouseEvent<unknown>, property: Key) => {
		const isAsc = orderBy === property && order === "asc";
		setOrder(isAsc ? "desc" : "asc");
		setOrderBy(property);
	};

	const sortedData = useMemo(() => {
		return stableSort(patientsData, getComparator(order, orderBy));
	}, [order, orderBy, patientsData]);

	return (
		<TableContainer
			sx={{
				width,
				height,
				transition: (theme) =>
					theme.transitions.create(["width", "height"], {
						duration: theme.transitions.duration.standard,
					}),
				overflow: "auto",
			}}
		>
			<Table stickyHeader sx={{ minWidth: 650, borderCollapse: "separate", borderSpacing: "2px" }} aria-label="patient data table">
				<EnhancedTableHead
					order={order}
					orderBy={orderBy}
					onRequestSort={handleRequestSort}
					isHospitalDemo={isHospitalDemo}
					isRealHospitalDemo={isRealHospitalDemo}
				/>
				<TableBody>
					{sortedData.map((patient) => (
						<TableRow
							key={patient.patientId}
							sx={{
								"&:last-child td, &:last-child th": { border: 0 },
								"&:hover": {
									backgroundColor: "#f5f5f5",
								},
								backgroundColor: "white",
							}}
						>
							<TableCell component="th" scope="row" sx={cellsSx}>
								{hidePatientNames ? maskPatientName(patient.patientName) : patient.patientName}
							</TableCell>

							{/* Hospital Demo columns */}
							{isHospitalDemo ? (
								<>
									<TableCell align="left" sx={cellsSx}>
										{patient.room}
									</TableCell>
									<TableCell align="left" sx={cellsSx}>
										{patient.medicalConditions.join(", ")}
									</TableCell>
									<TableCell align="left" sx={cellsSx}>
										{isRealHospitalDemo ? (
											// Real demo stacks "X days ago" under the date.
											<Box sx={{ display: "flex", flexDirection: "column", alignItems: "start", justifyContent: "start" }}>
												<Box>{patient.admissionDate?.toFormat("ccc, MMM d")}</Box>
												<Typography sx={{ fontSize: "12px", opacity: 0.6 }}>{patient.admissionDate?.toRelative()}</Typography>
											</Box>
										) : (
											// Legacy mock keeps main's side-by-side layout.
											<Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "start", gap: 1 }}>
												<Box sx={{ width: 100 }}>{patient.admissionDate?.toFormat("ccc, MMM d")}</Box>
												<Typography sx={{ fontSize: "12px", opacity: 0.6 }}>{patient.admissionDate?.toRelative()}</Typography>
											</Box>
										)}
									</TableCell>
									<TableCell align="left" sx={cellsSx}>
										{patient.nurseYesterday}
									</TableCell>
									<TableCell align="left" sx={cellsSx}>
										{patient.nurseToday}
									</TableCell>
									<TableCell align="left" sx={cellsSx}>
										{patient.patientScore?.toFixed(2)}
									</TableCell>
								</>
							) : (
								<>
									{/* Standard columns */}
									<TableCell
										align="left"
										sx={{
											...cellsSx,
											color: coverageToColor[patient.coverageType],
										}}
									>
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 1,
											}}
										>
											<Box
												sx={{
													borderRadius: "50%",
													backgroundColor: coverageToColor[patient.coverageType],
													width: 8,
													height: 8,
												}}
											/>
											{patient.coverageType}
										</Box>
									</TableCell>
									<TableCell align="left" sx={cellsSx}>
										{patient.medicalConditions.join(", ")}
									</TableCell>
									<TableCell align="left" sx={cellsSx}>
										<Box
											sx={{
												display: "flex",
												alignItems: "baseline",
												justifyContent: "start",
												gap: 1,
											}}
										>
											<Box
												sx={{
													width: 100,
												}}
											>
												{patient.admissionDate?.toFormat("ccc, MMM d")}
											</Box>
											<Typography
												sx={{
													fontSize: "12px",
													opacity: 0.6,
												}}
											>
												{patient.admissionDate?.toRelative()}
											</Typography>
										</Box>
									</TableCell>
									<TableCell align="left" sx={cellsSx}>
										{Math.round(patient?.chanceInTheFacility ?? 0)}%
									</TableCell>
								</>
							)}
						</TableRow>
					))}
				</TableBody>
			</Table>
		</TableContainer>
	);
};
